import React, { useRef, useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Save, ShoppingBag, Sticker, Hammer, Move, Users } from "lucide-react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { designAssets } from "../lib/designAssets";
import { MATERIALS, clampFrac } from "../components/studio/constants";
import { useLayerInteractions } from "../components/studio/useLayerInteractions";
import { renderDesignPreview } from "../components/studio/renderPreview";
import { StudioLeftPanel } from "../components/studio/StudioLeftPanel";
import { CanvasLayer } from "../components/studio/CanvasLayer";
import { LayerInspector } from "../components/studio/LayerInspector";

export default function DesignStudio() {
  const canvasRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ w: 500, h: 375 });
  const [params, setParams] = useSearchParams();
  const initialMode = params.get("mode") === "sticker" ? "sticker" : "engraving";
  const [mode, setMode] = useState(initialMode);
  const [material, setMaterial] = useState(MATERIALS[0]);
  const [stickerShape, setStickerShape] = useState("rounded"); // rounded | circle | custom
  const [plaqueShape, setPlaqueShape] = useState("rectangle"); // rectangle | circle
  const stickerColor = mode === "sticker";
  const defaultTextColor = stickerColor ? "#1a1a1a" : "#F3E8CF";
  // Layers now store x/y as a FRACTION of the canvas (0..1), so they stay inside bounds on every screen size.
  const [layers, setLayers] = useState([
    { id: "l1", type: "text", text: stickerColor ? "Your Sticker" : "Your Name", x: 0.5, y: 0.5, size: 48, rotation: 0, font: "serif", color: defaultTextColor },
  ]);
  const [selectedIds, setSelectedIds] = useState(() => new Set(["l1"]));
  const [lastSelectedId, setLastSelectedId] = useState("l1");
  const [multiMode, setMultiMode] = useState(false);
  const setSelected = (id) => {
    if (id == null) { setSelectedIds(new Set()); setLastSelectedId(null); return; }
    setSelectedIds(new Set([id]));
    setLastSelectedId(id);
  };
  const selectLayer = (id, additive = false) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const addMode = additive || multiMode;
      if (addMode) {
        if (next.has(id) && next.size > 1) next.delete(id);
        else next.add(id);
      } else {
        next.clear();
        next.add(id);
      }
      return next;
    });
    setLastSelectedId(id);
  };
  const nav = useNavigate();
  const { user } = useAuth();

  // Track canvas pixel size (updates on viewport resize / orientation change)
  useEffect(() => {
    const node = canvasRef.current;
    if (!node) return;
    const update = () => {
      const r = node.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) setCanvasSize({ w: r.width, h: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(node);
    window.addEventListener("resize", update);
    return () => { ro.disconnect(); window.removeEventListener("resize", update); };
  }, [mode, plaqueShape, stickerShape]);

  // One-time multi-select onboarding (per-browser)
  useEffect(() => {
    if (localStorage.getItem("ezjem_multi_tip_v1")) return;
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    const t = setTimeout(() => {
      toast.message(
        isTouch
          ? "Tip: Tap the Multi-select button to tap several layers and move them together."
          : "Tip: Hold Shift and click layers to select several — then drag any one to move the whole group.",
        { duration: 7000 }
      );
      localStorage.setItem("ezjem_multi_tip_v1", "1");
    }, 1500);
    return () => clearTimeout(t);
  }, []);


  const switchMode = (newMode) => {
    setMode(newMode);
    const next = new URLSearchParams(params);
    if (newMode === "sticker") next.set("mode", "sticker"); else next.delete("mode");
    setParams(next, { replace: true });
    // Flip default text color for existing text layers to stay readable
    setLayers((prev) => prev.map((l) => l.type === "text" ? { ...l, color: newMode === "sticker" ? "#1a1a1a" : "#F3E8CF" } : l));
  };

  const activeLayer = selectedIds.size === 1 ? layers.find((l) => l.id === lastSelectedId) : null;

  const updateLayer = (id, patch) =>
    setLayers((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));

  const addText = () => {
    const id = "l" + Date.now();
    setLayers((p) => [...p, { id, type: "text", text: "New text", x: 0.5, y: 0.5, size: 40, rotation: 0, font: "serif", color: "#F3E8CF" }]);
    setSelected(id);
  };

  const applyBabyPlaquePreset = () => {
    setPlaqueShape("circle");
    const wood = MATERIALS.find((m) => m.key === "wood") || MATERIALS[0];
    setMaterial(wood);
    const nameId = "l_name_" + Date.now();
    const subId = "l_sub_" + Date.now();
    setLayers([
      { id: nameId, type: "text", text: "WESTLEY", x: 0.5, y: 0.38, size: 56, rotation: 0, font: "serif", color: "#F3E8CF" },
      { id: subId,  type: "text", text: "BORN WITH LOVE", x: 0.5, y: 0.72, size: 20, rotation: 0, font: "serif", color: "#F3E8CF" },
    ]);
    setSelected(nameId);
  };

  const centerSelected = () => {
    if (selectedIds.size === 0) return;
    if (selectedIds.size === 1) {
      updateLayer(lastSelectedId, { x: 0.5, y: 0.5 });
      return;
    }
    // Multi: shift the group so its centroid lands at (0.5, 0.5)
    const sels = layers.filter((l) => selectedIds.has(l.id));
    const cx = sels.reduce((s, l) => s + (l.x ?? 0.5), 0) / sels.length;
    const cy = sels.reduce((s, l) => s + (l.y ?? 0.5), 0) / sels.length;
    const dx = 0.5 - cx, dy = 0.5 - cy;
    setLayers((prev) => prev.map((l) =>
      selectedIds.has(l.id) ? { ...l, x: clampFrac((l.x ?? 0.5) + dx), y: clampFrac((l.y ?? 0.5) + dy) } : l
    ));
  };

  const deleteSelected = () => {
    if (selectedIds.size === 0) return;
    setLayers((prev) => prev.filter((l) => !selectedIds.has(l.id)));
    setSelectedIds(new Set());
    setLastSelectedId(null);
  };


  const { startDrag, moveDrag, endDrag, onCanvasTouchStart, onCanvasTouchMove, onCanvasTouchEnd } = useLayerInteractions({
    layers, setLayers, selectedIds, setSelectedIds, setLastSelectedId, setSelected, multiMode, canvasRef,
  });


  const addImage = (dataUrl, fileName) => {
    const id = "l" + Date.now();
    setLayers((p) => [...p, { id, type: "image", src: dataUrl, file_name: fileName || "upload", x: 0.5, y: 0.5, width: Math.round(Math.min(220, canvasSize.w * 0.45)), height: Math.round(Math.min(220, canvasSize.w * 0.45)), rotation: 0, opacity: 1, engraved: mode !== "sticker", contrast: 1.6, brightness: 0.9 }]);
    setSelected(id);
  };

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => addImage(ev.target.result, file.name);
    reader.readAsDataURL(file);
  };

  const removeLayer = (id) => {
    setLayers((p) => p.filter((l) => l.id !== id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    if (lastSelectedId === id) setLastSelectedId(null);
  };

  const saveProject = async () => {
    if (!user) { toast.error("Sign in to save designs"); nav("/login"); return; }
    try {
      await api.post("/design-projects", { name: `Design ${new Date().toLocaleString()}`, material: material.name, canvas_data: { layers, material: material.key } });
      toast.success("Design saved to your account");
    } catch { toast.error("Could not save"); }
  };

  const captureDesignPreview = () => canvasRef.current
    ? renderDesignPreview(canvasRef.current, { layers, mode, material, stickerShape, plaqueShape })
    : Promise.resolve(null);


  const orderThis = async () => {
    const tier = params.get("tier");
    const previewImage = await captureDesignPreview();
    designAssets.originals = layers.filter((l) => l.type === "image" && l.src?.startsWith("data:")).map((l) => ({ name: l.file_name || "upload", data_url: l.src }));
    // Strip heavy image sources out of layers for sessionStorage — the preview image already captures the composition.
    // We keep type / position / size / rotation / text / font / color / engrave info so admin and reorder flows know what the design is.
    const lightLayers = layers.map((l) => {
      if (l.type === "image") {
        const { src, ...rest } = l;
        return { ...rest, has_image: true };
      }
      return l;
    });
    const payload = {
      mode,
      material: mode === "sticker" ? `${stickerShape} sticker · vinyl` : material.name,
      layers: lightLayers,
      preview_image: previewImage,
    };
    try {
      sessionStorage.setItem("ezjem_design", JSON.stringify(payload));
    } catch (err) {
      // Still too big? Drop the preview from storage and keep going — the order can still be submitted.
      console.warn("Design too large for sessionStorage, retrying without preview", err);
      try {
        sessionStorage.setItem("ezjem_design", JSON.stringify({ ...payload, preview_image: null }));
        // Also expose the preview on window for the next page to pick up
        window.__ezjem_preview = previewImage;
      } catch (err2) { console.warn("sessionStorage still failing", err2); }
    }
    const qs = new URLSearchParams();
    if (tier) qs.set("tier", tier);
    nav(`/custom-order${qs.toString() ? "?" + qs.toString() : ""}`);
  };

  return (
    <div data-testid="design-studio-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="uppercase text-xs tracking-[0.3em] font-bold" style={{color: "var(--secondary)"}}>Design Studio</div>
          <h1 className="font-serif text-5xl sm:text-6xl mt-3 leading-none" style={{color: "var(--primary)"}}>Compose. Preview. Order.</h1>
        </div>
        <div data-testid="mode-switcher" className="inline-flex rounded-full border hairline bg-white p-1">
          <button data-testid="mode-engraving" onClick={() => switchMode("engraving")}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] transition ${mode === "engraving" ? "bg-[color:var(--primary)] text-white" : "text-[color:var(--text-muted)] hover:text-[color:var(--primary)]"}`}>
            <Hammer size={13}/> Engraving
          </button>
          <button data-testid="mode-sticker" onClick={() => switchMode("sticker")}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] transition ${mode === "sticker" ? "bg-[color:var(--primary)] text-white" : "text-[color:var(--text-muted)] hover:text-[color:var(--primary)]"}`}>
            <Sticker size={13}/> Stickers
          </button>
        </div>
      </div>
      <p className="mt-4 max-w-xl text-[color:var(--text-muted)] leading-relaxed">
        {mode === "sticker"
          ? "Design your sticker on a kiss-cut white sheet — pick a shape, add artwork and text, and we'll produce it in full color on durable vinyl."
          : "Add text and photos, drag to position, resize, rotate — see how your engraving will look on real material before you commit."}
      </p>

      <div className="mt-10 grid lg:grid-cols-[320px_1fr_280px] gap-6">
        {/* LEFT PANEL - material or sticker sheet */}
        <StudioLeftPanel mode={mode} stickerShape={stickerShape} setStickerShape={setStickerShape} material={material} setMaterial={setMaterial}
          plaqueShape={plaqueShape} setPlaqueShape={setPlaqueShape} applyBabyPlaquePreset={applyBabyPlaquePreset} addText={addText}
          onUpload={onUpload} layers={layers} selectedIds={selectedIds} selectLayer={selectLayer} removeLayer={removeLayer}/>

        {/* CENTER - CANVAS */}
        <div className="relative">
          <div className={`relative w-full aspect-[4/3] ${(mode === "sticker" || (mode === "engraving" && plaqueShape === "circle")) ? "p-6 flex items-center justify-center bg-[color:var(--muted)] rounded-lg border hairline" : ""}`}>
            <div ref={canvasRef} data-testid="design-canvas"
              onTouchStart={onCanvasTouchStart}
              onTouchMove={onCanvasTouchMove}
              onTouchEnd={onCanvasTouchEnd}
              className={`relative overflow-hidden ${mode === "sticker" ? "shadow-[0_8px_30px_rgba(44,53,57,0.14)]" : (plaqueShape === "circle" ? "shadow-[0_8px_30px_rgba(44,53,57,0.14)]" : "w-full h-full rounded-lg border hairline shadow-[0_8px_30px_rgba(44,53,57,0.10)]")}`}
              style={mode === "sticker"
                ? {
                    width: stickerShape === "circle" ? "min(80%, 420px)" : "90%",
                    aspectRatio: stickerShape === "circle" ? "1 / 1" : "4 / 3",
                    background: "#FFFFFF",
                    borderRadius: ({rounded: "16px", circle: "50%", square: "4px", diecut: "32px"})[stickerShape] || "16px",
                    outline: "2px dashed rgba(0,0,0,0.25)",
                    outlineOffset: "-6px",
                  }
                : (plaqueShape === "circle"
                    ? {
                        width: "min(80%, 440px)",
                        aspectRatio: "1 / 1",
                        backgroundImage: material.bg,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        borderRadius: "50%",
                      }
                    : {backgroundImage: material.bg, backgroundSize: "cover", backgroundPosition: "center"})
              }>
              {mode === "engraving" && <div className="absolute inset-0 grain pointer-events-none" style={plaqueShape === "circle" ? {borderRadius: "50%"} : undefined}/>}
            {layers.map((l) => (
              <CanvasLayer key={l.id} l={l} canvasSize={canvasSize} selected={selectedIds.has(l.id)} startDrag={startDrag} moveDrag={moveDrag} endDrag={endDrag}/>
            ))}
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-3 justify-between items-center">
            <div className="flex flex-wrap gap-2 items-center">
              {selectedIds.size > 0 && (
                <Button data-testid="center-layer-btn" onClick={centerSelected} variant="outline" size="sm" className="rounded-full">
                  <Move size={14} className="mr-2"/> {selectedIds.size > 1 ? "Re-center group" : "Re-center selected"}
                </Button>
              )}
              <Button
                data-testid="multi-select-toggle"
                onClick={() => {
                  const next = !multiMode;
                  setMultiMode(next);
                  if (next) toast.message("Multi-select ON — tap any layers to add them to the group. They'll all move together.", { duration: 4000 });
                  else {
                    toast.message("Multi-select OFF — back to single layer selection.", { duration: 2500 });
                    setSelected(lastSelectedId);
                  }
                }}
                variant={multiMode ? "default" : "outline"}
                size="sm"
                className={`rounded-full ${multiMode ? "btn-metal" : ""}`}
              >
                <Users size={14} className="mr-2"/>
                {multiMode ? `Multi-select ON · ${selectedIds.size}` : "Multi-select"}
              </Button>
              {selectedIds.size > 1 && (
                <span data-testid="selection-count" className="text-xs text-[color:var(--text-muted)]">{selectedIds.size} layers selected</span>
              )}
            </div>
            <div className="hidden md:flex items-center gap-2 text-[10px] text-[color:var(--text-muted)]">
              <kbd className="px-1.5 py-0.5 rounded border hairline bg-white font-mono">⇧</kbd> + click to add to group
              <span>·</span>
              <kbd className="px-1.5 py-0.5 rounded border hairline bg-white font-mono">↑↓←→</kbd> nudge
            </div>
            <div className="flex flex-wrap gap-3 ml-auto">
              <Button data-testid="save-design-btn" onClick={saveProject} variant="outline" className="rounded-full"><Save size={16} className="mr-2"/> Save design</Button>
              <Button data-testid="order-design-btn" onClick={orderThis} className="btn-primary rounded-full"><ShoppingBag size={16} className="mr-2"/> Order this design</Button>
            </div>
          </div>
        </div>

        <LayerInspector selectedIds={selectedIds} activeLayer={activeLayer} updateLayer={updateLayer} deleteSelected={deleteSelected}/>
      </div>
    </div>
  );
}
